import { useState } from 'react'
import type { MoneyTotals, PaymentTransaction } from '@workflow/contracts'
import { Button, ConfirmButton, EmptyNote, Loading, TextField, cn } from '@workflow/ui'
import { formatTimestamp } from '@/lib/format'
import { formatMoney } from './jobsStatus'
import { paymentClientLabel } from '@/features/clients/clientStatus'

/**
 * The shared ledger readouts: the totals strip and the trail of movements,
 * newest first. The job page no longer mounts a ledger section — these stay
 * for the client page and the member's work, where recorded payments still
 * read. Nothing here processes money; the trail is read-only except for the
 * void it offers where the reader may void, and a voided row stays on the
 * trail with its reason.
 */

/** Where a subject stands: received, paid out, per currency — derived from live rows only. */
export function MoneyTotalsStrip({ totals, loading }: { totals: MoneyTotals[] | undefined; loading: boolean }) {
  if (loading) return <Loading label="Counting payments" />
  if (!totals || totals.length === 0) {
    return <p className="text-caption text-muted-foreground">No payments recorded yet.</p>
  }
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3">
      {totals.map((row) => (
        <div key={row.currency} className="flex flex-col gap-0.5">
          <span className="text-micro text-muted-foreground uppercase">{row.currency}</span>
          <span className="text-ui">
            <span className="font-medium text-foreground">{formatMoney(row.inboundMinor)}</span>
            <span className="text-muted-foreground"> received · </span>
            <span className="font-medium text-foreground">{formatMoney(row.outboundMinor)}</span>
            <span className="text-muted-foreground"> paid</span>
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * The trail: every movement, newest first. A voided row stays on the trail,
 * struck through with its reason — the ledger's past is truthful about what
 * was once believed. Voiding asks why before it means it; a reason is the
 * server's own requirement.
 */
export function MoneyTrail({
  payments,
  loading,
  onVoid,
  voiding,
}: {
  payments: PaymentTransaction[] | undefined
  loading: boolean
  /** Absent where the reader may not void — the trail is read-only there. */
  onVoid?: (voiding: { id: string; reason: string }) => void
  voiding?: boolean
}) {
  const [askingId, setAskingId] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  if (loading) return <Loading label="Loading payments" />
  if (!payments || payments.length === 0) {
    return <EmptyNote title="No payments yet." detail="Recorded payments appear here." />
  }

  const ask = (id: string) => {
    setAskingId(id)
    setReason('')
  }
  const confirm = (id: string) => {
    if (reason.trim().length < 3) return
    onVoid?.({ id, reason: reason.trim() })
    setAskingId(null)
    setReason('')
  }

  return (
    <ul className="flex flex-col">
      {payments.map((payment) => {
        const dead = payment.voidedAt !== null
        const who = payment.direction === 'inbound' ? paymentClientLabel(payment, 'the client') : (payment.playerName ?? 'a player')
        const asking = askingId === payment.id
        return (
          <li key={payment.id} className="border-b border-hairline last:border-0">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-1 py-2.5">
              <span className={cn('text-ui font-medium', dead ? 'text-muted-foreground line-through' : 'text-foreground')}>
                {payment.direction === 'inbound' ? '+' : '−'}
                {formatMoney(payment.amount)} {payment.currency}
              </span>
              <span className="text-caption text-muted-foreground">
                {payment.direction === 'inbound' ? 'from' : 'to'} {who}
                {payment.method ? ` · ${payment.method}` : ''}
                {payment.referenceNumber ? ` · ${payment.referenceNumber}` : ''}
              </span>
              <span className="ml-auto text-caption text-muted-foreground">
                {formatTimestamp(payment.paymentDate)} · by {payment.recordedByName ?? payment.recordedBy}
              </span>
              {dead ? (
                <span className="text-caption text-danger" title={`Voided by ${payment.voidedByName ?? 'the studio'}`}>
                  voided{payment.voidReason ? ` — ${payment.voidReason}` : ''}
                </span>
              ) : asking || !onVoid ? null : (
                <Button size="xs" variant="ghost" onClick={() => ask(payment.id)}>
                  Void
                </Button>
              )}
            </div>
            {asking && onVoid ? (
              <div className="flex flex-wrap items-center gap-2 px-1 pb-3">
                <TextField
                  value={reason}
                  placeholder="Reason for voiding (required)"
                  className="max-w-80"
                  onChange={(event) => setReason(event.target.value)}
                />
                <ConfirmButton
                  label="Void it"
                  ask={{
                    title: `Void this ${formatMoney(payment.amount)} ${payment.currency} payment?`,
                    detail: 'It stays on the record, marked void, and stops counting. This cannot be undone.',
                    confirmLabel: 'Void',
                  }}
                  busy={voiding === true}
                  onConfirm={() => confirm(payment.id)}
                />
                <Button size="sm" variant="ghost" onClick={() => setAskingId(null)}>
                  Cancel
                </Button>
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
